import React, { useState } from 'react';
import { Button } from '../../../shared/components/ui';
import { recipeService } from '../services/recipeService';
import { useTranslation } from 'react-i18next';

const GenerateRecipeModal = ({ isOpen, onClose, onRecipeGenerated }) => {
    const { t } = useTranslation();
    const [description, setDescription] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!description.trim()) {
            setError(t('menu.errors.emptyDescription'));
            return;
        }

        setError('');
        setIsGenerating(true);

        try {
            const response = await recipeService.generateRecipe(description);

            if (response.success && response.recipe) {
                // Llamar al callback con la receta generada
                onRecipeGenerated(response.recipe);
                // Limpiar el formulario y cerrar
                setDescription('');
                onClose();
            } else {
                setError(t('menu.errors.generationFailed'));
            }
        } catch (error) {
            setError(error.message || t('menu.errors.generationFailed'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleClose = () => {
        if (!isGenerating) {
            setDescription('');
            setError('');
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-bold text-gray-800">
                            {t('menu.generate.title')}
                        </h2>
                        <button
                            onClick={handleClose}
                            disabled={isGenerating}
                            className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
                        >
                            ×
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('menu.generate.description')}
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={isGenerating}
                                placeholder={t('menu.generate.placeholder')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                rows={6}
                                maxLength={500}
                            />
                            <p className="text-sm text-gray-500 mt-1">
                                {description.length}/500 caracteres
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={isGenerating}
                            >
                                {t('menu.generate.cancel') || t('common.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isGenerating || !description.trim()}
                            >
                                {isGenerating ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('menu.generate.loading')}
                                    </span>
                                ) : (
                                    t('menu.generate.button')
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default GenerateRecipeModal;
